// Small shared UI pieces: Modal (native <dialog>), toasts, spinner, empty state.
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { IX } from "./icons";

export function Modal({
  open,
  onClose,
  children,
  label,
  wide,
  sheet = true,
  dismissable = true,
}: {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  label: string;
  wide?: boolean;
  sheet?: boolean;
  dismissable?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog
      ref={ref}
      className={`modal${wide ? " wide" : ""}${sheet ? " sheet" : ""}`}
      aria-label={label}
      onCancel={(e) => {
        e.preventDefault();
        if (dismissable) onClose();
      }}
      onClick={(e) => {
        if (dismissable && e.target === ref.current) onClose(); // backdrop click
      }}
    >
      {open && (
        <div className="modal-body">
          {dismissable && (
            <button className="iconbtn sm" style={{ position: "absolute", top: 14, right: 14 }} onClick={onClose} aria-label="Close">
              <IX />
            </button>
          )}
          {children}
        </div>
      )}
    </dialog>
  );
}

// ---- toasts ----

interface Toast { id: number; text: string; kind: "info" | "error" }
const ToastCtx = createContext<(text: string, kind?: Toast["kind"]) => void>(() => {});
export const useToast = () => useContext(ToastCtx);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const show = useCallback((text: string, kind: Toast["kind"] = "info") => {
    const id = Math.random();
    setToasts((t) => [...t.slice(-2), { id, text, kind }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), kind === "error" ? 5000 : 2800);
  }, []);
  return (
    <ToastCtx.Provider value={show}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.kind}`}>
            {t.text}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export const Spinner = ({ label = "Loading" }: { label?: string }) => <span className="spinner" role="progressbar" aria-label={label} />;

export function Empty({ title, children, art }: { title: string; children?: ReactNode; art?: ReactNode }) {
  return (
    <div className="card flat" style={{ textAlign: "center", padding: "40px 24px" }}>
      {art && <div style={{ display: "grid", placeItems: "center", marginBottom: 12 }}>{art}</div>}
      <h2>{title}</h2>
      {children && <div className="sub" style={{ marginTop: 8 }}>{children}</div>}
    </div>
  );
}

/** Keyboard shortcut helper: ignores keys typed into inputs unless `inInputs`. */
export function useHotkey(key: string, handler: (e: KeyboardEvent) => void, opts: { meta?: boolean; inInputs?: boolean; enabled?: boolean } = {}) {
  const h = useRef(handler);
  h.current = handler;
  useEffect(() => {
    if (opts.enabled === false) return;
    const on = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() !== key.toLowerCase()) return;
      if (!!opts.meta !== (e.metaKey || e.ctrlKey)) return;
      const t = e.target as HTMLElement | null;
      const typing = t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable);
      if (typing && !opts.inInputs) return;
      if (document.querySelector("dialog[open]") && !opts.meta && !t?.closest("dialog")) return;
      h.current(e);
    };
    window.addEventListener("keydown", on);
    return () => window.removeEventListener("keydown", on);
  }, [key, opts.meta, opts.inInputs, opts.enabled]);
}
